import { AnyAction } from "redux";
import {
    TEMPLATE_CREATE_COMPLETED,
    TEMPLATE_CREATE_CONNECTED,
    TEMPLATE_CREATE_CONNECTING,
    TEMPLATE_CREATE_EVENT,
    TEMPLATE_CREATE_FAILED,
    TEMPLATE_CREATE_RESET,
} from "../types";
import {
    AIRun,
    beginAIRun,
    initialAIRun,
    applyAIEvent,
} from "../../components/shared/brand/aiProgress";

/**
 * Template creation progress - an `AIRun`, like every other AI flow.
 *
 * This reducer used to keep its own step list in its own shape ("Pending" /
 * "In Progress" / "Completed" as display strings), seeded with five invented
 * steps and then patched by whichever real events happened to match one by
 * name. It is the shared model now: the engine's `step_started` /
 * `step_progress` / `step_completed` frames go in raw and `applyAIEvent`
 * decides what the run looks like.
 *
 * Nothing is pre-drawn. A stage exists once the engine has mentioned it, and
 * `percent` stays null until the engine sends a real one - which the panel
 * renders as a moving sliver rather than a number nobody measured.
 */
interface TemplateState {
    run: AIRun;
}

const initial: TemplateState = { run: initialAIRun };

export function templateReducer(state: TemplateState = initial, action: AnyAction): TemplateState {
    switch (action.type) {
        case TEMPLATE_CREATE_RESET:
            return initial;

        case TEMPLATE_CREATE_CONNECTING:
            return { ...state, run: beginAIRun() };

        /* Deliberately a no-op on the run. `beginAIRun` already put it in
           `connecting`, and the FIRST engine frame is what moves it to
           `running` - a socket handshake is not progress on the work. */
        case TEMPLATE_CREATE_CONNECTED:
            return state;

        /* One door for every engine frame - the envelope goes in as it arrived. */
        case TEMPLATE_CREATE_EVENT:
            return { ...state, run: applyAIEvent(state.run, action.payload?.raw, action.payload?.eventName) };

        case TEMPLATE_CREATE_COMPLETED:
            return {
                ...state,
                run: {
                    ...state.run,
                    status: (action.payload?.ok === false ? 'failed' : 'completed') as AIRun['status'],
                    message: action.payload?.message || state.run.message,
                },
            };

        case TEMPLATE_CREATE_FAILED:
            return {
                ...state,
                run: {
                    ...state.run,
                    status: 'failed' as const,
                    error: action.payload?.error || action.payload?.message || null,
                },
            };

        default:
            return state;
    }
}
